import { createI18n } from 'vue-i18n'
import { fr as vuetifyFr, en as vuetifyEn, de as vuetifyDe, es as vuetifyEs, nl as vuetifyNl } from 'vuetify/locale'

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface MessageTree extends Record<string, MessageValue> {}
type MessageValue = string | MessageTree | MessageValue[]

const fileNameToLocaleModuleDict = import.meta.glob<{ default: MessageTree }>('./locales/*.json', {
  eager: true,
})

const messages: Record<string, MessageTree> = {}
Object.entries(fileNameToLocaleModuleDict)
  .map(([fileName, localeModule]) => {
    const fileNameParts = fileName.split('/')
    const fileNameWithoutPath = fileNameParts[fileNameParts.length - 1]
    const localeName = fileNameWithoutPath.split('.json')[0]

    return [localeName, localeModule.default] as const
  })
  .forEach((localeNameLocaleMessagesTuple) => {
    messages[localeNameLocaleMessagesTuple[0]] = localeNameLocaleMessagesTuple[1]
  })

// Vuetify's built-in components (VDataTable, VDatePicker...) resolve their
// default text props as vue-i18n keys under $vuetify (see plugins/vuetify.ts
// createVueI18nAdapter) — without this merge those keys don't exist in our
// own message files and vue-i18n's `missing` handler below returns the raw
// key string, which is worse than Vuetify's English defaults.
const vuetifyLocalePacks: Record<string, MessageTree> = {
  fr: vuetifyFr,
  en: vuetifyEn,
  de: vuetifyDe,
  es: vuetifyEs,
  nl: vuetifyNl,
}
Object.entries(vuetifyLocalePacks).forEach(([localeName, vuetifyMessages]) => {
  messages[localeName] = { ...(messages[localeName] ?? {}), $vuetify: vuetifyMessages }
})

const isDevelopment = import.meta.env.MODE === 'development'
const reportedMissingKeys = new Set<string>()

function reportMissingKey(locale: string, key: string): void {
  const cacheKey = `${locale}:${key}`
  if (reportedMissingKeys.has(cacheKey)) {
    return
  }
  reportedMissingKeys.add(cacheKey)
  console.warn(`[i18n] missing key "${key}" for locale "${locale}"`)
}

export const LOCALE_STORAGE_KEY = 'mylimni_locale'

const SUPPORTED_LOCALES = ['fr', 'en', 'de', 'es', 'nl']

/** Langue mémorisée, si elle correspond à un fichier de traduction existant. */
function initialLocale(): string {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (saved && SUPPORTED_LOCALES.includes(saved)) return saved
  } catch {
    // localStorage indisponible : on garde la langue par défaut.
  }
  return 'fr'
}

const i18n = createI18n({
  legacy: false,
  locale: initialLocale(),
  fallbackLocale: ['fr', 'en'],
  missingWarn: isDevelopment,
  fallbackWarn: isDevelopment,
  missing: (locale, key) => {
    if (isDevelopment) {
      reportMissingKey(String(locale), key)
    }
    return key
  },
  messages,
})

export default i18n

export const tGlobal = (key: string, params?: Record<string, unknown>): string => {
  try {
    // @ts-expect-error - i18n.global is available in composition mode
    return i18n.global.t(key, params as unknown) as string
  } catch (error) {
    console.warn("i18n key not found:", key, error)
    return key
  }
}
